/**
 * The numbers, names and addresses the shared layer is tuned by: the API
 * client, the caches, the device's own storage, the update checks and the
 * Google Photos picker. (The outlook's fallback grading went with audit
 * batch 4: the app no longer judges a week, so it keeps no copy.)
 *
 * Kept in one place, out of the code that uses them, so that each can be
 * found and changed without reading the module behind it, and so that they
 * can later come from the database instead (each carries its kept-values group, scripts/kept-values.md).
 * Constants only: no functions, and no imports, so anything may import this
 * without a cycle.
 *
 * Storage keys and event names are tagged too, for uniformity, but they are
 * names browsers already hold: renaming one forgets what every gardener's
 * device had saved under the old one. Change them only on purpose.
 *
 * Not here: what the owner already changes in the Grow settings panel
 * (src/shared/lib/growSettings), the app-wide query default stale time
 * (src/config/queryClient), the older catalogues in src/config/config.ts
 * (APP_CONSTANTS and the rest) and the retired weather file,
 * and the server's limits (the longest note, stage name, gift message and the rest), which
 * the server sends with the Grow settings (limits.*, lib/growSettings).
 *
 * Used by: some forty files: the API client and most of shared/api, the
 * contexts, shared/lib (arrival, home page, update checks), config/env-vars
 * (PRODUCTION_API_URL), apiClient (LOCAL_API_URL), config/firebase (FIREBASE_OWN_AUTH_HOSTS) and
 * config/queryClient (QUERY_RETRY_COUNT), and several features.
 *
 * @module tunables/shared
 */

// ---------------------------------------------------------------------------
// The API and the caches
// ---------------------------------------------------------------------------

/**
 * The backend's API in production, used when the build was given no
 * VITE_API_URL (env-vars.ts). An address, not a number.
 * @kept K1
 */
export const PRODUCTION_API_URL = 'https://seal-app-zys9t.ondigitalocean.app/api';

/**
 * The local backend's API, the same base as development's: what apiClient
 * falls back to when the build was given no VITE_API_URL (with a warning in
 * development). An address, not a number.
 * @kept K1
 */
export const LOCAL_API_URL = 'http://localhost:3001/api';

/**
 * How long any one API request may take before it is abandoned, in
 * milliseconds (apiClient). Longer than any call legitimately takes, uploads
 * included; without it a stalled request never comes back.
 * @kept K1
 */
export const REQUEST_TIMEOUT_MS = 30_000;

/**
 * Where a gardener writes for help: the address the standard failure words
 * name, as text and as a mailto link (shared/lib/serverFailure,
 * shared/components/ServerFailure). An address, not a number.
 * @kept K7
 */
export const SUPPORT_EMAIL = 'hello@gardengameplan.com';

/**
 * The privacy policy, on the website. It covers Grow and the site, and is the
 * address Google's OAuth verification of the Photos Picker points at. Linked
 * from the sign-in page and the foot of the profile.
 * @kept K7
 */
export const PRIVACY_URL = 'https://gardengameplan.com/privacy';

/**
 * The terms of use, on the website, beside the privacy policy. Linked from the
 * signed-out landing page's foot.
 * @kept K7
 */
export const TERMS_URL = 'https://gardengameplan.com/terms';

/**
 * How many times a failed query is tried again before its error shows: the
 * app-wide default (queryClient) and the hooks that set it themselves
 * (useGardenPostcode, usePlantOutlook). A count.
 * @kept K1
 */
export const QUERY_RETRY_COUNT = 1;

/**
 * How long the garden's plant outlook stays fresh in the client, in
 * milliseconds (usePlantOutlook). The server caches it for an hour.
 * @kept K1
 */
export const OUTLOOK_STALE_TIME_MS = 30 * 60 * 1000;

/**
 * The React Query key the garden's plant outlook is cached under, followed by
 * the postcode and the unit (usePlantOutlook). Saving a plant or a bed
 * invalidates it by this first word; Today's Coming up caches under it too.
 * @kept K1
 */
export const PLANT_OUTLOOK_QUERY_KEY = 'plant-outlook';

// ---------------------------------------------------------------------------
// The device's own storage, and events between components
// ---------------------------------------------------------------------------

/**
 * localStorage: the garden this browser is tending, when it is not the
 * signer's own (gardenMembers).
 * @kept K1
 */
export const CHOSEN_GARDEN_STORAGE_KEY = 'bloom.garden';

/**
 * sessionStorage: set when the server said this person is no longer a member
 * of the garden they were tending, so the page the reload lands on can say
 * why it is their own garden now (gardenMembers, Layout).
 * @kept K1
 */
export const GARDEN_LEFT_SESSION_KEY = 'grow.gardenLeft';

/**
 * localStorage: the share code that brought this visitor, followed from the
 * share page into signup (shares, SharePage, CompleteSignup).
 * @kept K1
 */
export const REFERRAL_CODE_STORAGE_KEY = 'grow.referredBy';

/**
 * localStorage: the name of the gardener whose share brought the visitor,
 * when that plant is public (shares, SharePage, CompleteSignup).
 * @kept K1
 */
export const REFERRAL_NAME_STORAGE_KEY = 'grow.referredByName';

/**
 * localStorage: when the share page was opened, in milliseconds since the
 * epoch, so an old referral stops counting.
 * @kept K1
 */
export const REFERRAL_AT_STORAGE_KEY = 'grow.referredAt';
/**
 * Set for this visit only (sessionStorage) when the address carried a share's
 * ?ref: the sign-up page says "Started from …'s garden" only then, not to
 * someone who came through Grow's own buttons with an older credit kept.
 */
export const REFERRED_THIS_VISIT_SESSION_KEY = 'grow.referredThisVisit';

/**
 * Set for this visit only (sessionStorage) when the address carried a
 * founding link's ?founding=: the landing page leads with the founding
 * welcome only then (owner, 2026-10-14: a code kept from a founding link
 * long ago showed the founder headline on every visit, even ?from=ig). The
 * code itself stays kept for sign-up.
 */
export const FOUNDING_THIS_VISIT_SESSION_KEY = 'grow.foundingThisVisit';

/**
 * localStorage: the share codes this browser has opened, so a visit is
 * counted once per browser rather than once per load (shares).
 * @kept K1
 */
export const SEEN_SHARES_STORAGE_KEY = 'grow.seenShares';

/**
 * How many opened share codes a browser remembers before forgetting the
 * oldest (shares). A count; past it, a very old link may be counted again.
 * @kept K1
 */
export const SEEN_SHARES_MAX = 200;

/**
 * The tallest a share page's picture is drawn, as a share of the screen's
 * height (FrameStrip, SharePage). A story frame is 1080 × 1920: drawn at the
 * column's full width it stood taller than a laptop screen and pushed the
 * gardener's words and the invitation out of sight. Capped here, it keeps
 * its shape and leaves the title above it in view. A CSS length in svh,
 * the small viewport's height, which does not jump as a phone's address
 * bar comes and goes.
 * @kept K5
 */
export const SHARE_PICTURE_MAX_HEIGHT = '60svh';

/**
 * sessionStorage: the name typed on the sign-up form, waiting for the Grow
 * account to be made with it (users, AuthContext, CompleteSignup).
 * @kept K1
 */
export const SIGNUP_NAME_SESSION_KEY = 'grow.signupName';

/**
 * localStorage: the founding code a ?founding= link brought, kept from that
 * visit until sign-up (or, for an account that already exists, until it is
 * claimed) (shared/lib/arrivalSource, CompleteSignup, FoundingClaim).
 * @kept K1
 */
export const FOUNDING_CODE_STORAGE_KEY = 'grow.foundingCode';

/**
 * localStorage: a gift code opened on /gift/redeem by somebody signed out,
 * kept until they sign in or sign up and it is applied to their account
 * (features/gift GiftClaim).
 * @kept K1
 */
export const GIFT_CODE_STORAGE_KEY = 'grow.giftCode';


/**
 * localStorage: the utm_source, utm_medium and utm_campaign of the first
 * tagged link this browser arrived by, as JSON, kept until sign-up
 * (shared/lib/arrivalSource, CompleteSignup).
 * @kept K1
 */
export const SIGNUP_UTM_STORAGE_KEY = 'grow.signupUtm';

/**
 * localStorage: which of the owner's own links this browser first arrived by,
 * her Instagram (?from=ig) or her Facebook (?from=fb), kept until sign-up,
 * which sends it so the account has the follower trial
 * (shared/lib/arrivalSource, CompleteSignup, the landing page).
 * @kept K1
 */
export const SIGNUP_FROM_STORAGE_KEY = 'grow.signupFrom';

/**
 * sessionStorage: this visit came to start a garden (a "Start a garden" link,
 * /?start=1, or a share's /?ref=CODE), so the sign-in page opens on signing
 * up; kept because the address is moved on to /today before the page draws
 * (shared/lib/arrivalSource, LoginPage).
 * @kept K1
 */
export const START_GARDEN_SESSION_KEY = 'grow.startGarden';

/**
 * localStorage: the language chosen on the sign-in page ("Español" /
 * "English"), for the screens before there is an account; without it the
 * browser's own language decides (shared/lib/startLanguage). Sign-up saves
 * it to the new account's preferences.
 * @kept K1
 */
export const START_LANGUAGE_STORAGE_KEY = 'grow.language';

/**
 * sessionStorage: marks a sign-in under way, across Google's redirect and
 * back, so it can land on Today (homePage).
 * @kept K1
 */
export const SIGNING_IN_SESSION_KEY = 'grow.signingIn';

/**
 * sessionStorage: marks this tab's session as launched, so a reload is not
 * taken for a launch (homePage).
 * @kept K1
 */
export const LAUNCHED_SESSION_KEY = 'grow.launched';

/**
 * sessionStorage: the build a reload was last made for, so a server still
 * sending the old app cannot loop the reload (pwaUpdate).
 * @kept K1
 */
export const RELOADED_FOR_SESSION_KEY = 'grow.reloadedFor';

/**
 * localStorage: how a plant card shows its photos, 'peek' or 'full'
 * (localPref CARD_PHOTOS_KEY; PlantCard, ProfilePreferences).
 * @kept K1
 */
export const CARD_PHOTOS_STORAGE_KEY = 'bloom.cardPhotos';

/**
 * localStorage: the theme last applied, read before the account's
 * preferences arrive so the first paint is already right (themeUtils).
 * @kept K1
 */
export const THEME_STORAGE_KEY = 'bloom-theme';

/**
 * localStorage: the themes as the server last sent them, made into CSS
 * ({ version, css, dark }), so the next first paint is in the database's
 * colours before GET /api/themes answers (shared/theme/themeTokens). index.html's
 * first-paint script reads the same key by name.
 */
export const THEME_TOKENS_STORAGE_KEY = 'grow-theme-tokens';

/**
 * The window event a device preference fires when it changes, so every
 * component reading the same key redraws at once (localPref).
 * @kept K1
 */
export const LOCAL_PREF_EVENT = 'grow:local-pref';

// ---------------------------------------------------------------------------
// Keeping the installed app current
// ---------------------------------------------------------------------------

/**
 * How often an open app asks the service worker whether sw.js has changed,
 * in milliseconds (pwaUpdate). Long enough not to badger the network, short
 * enough that a day's work is not missed.
 * @kept K1
 */
export const SW_UPDATE_CHECK_MS = 60 * 60 * 1000;

/**
 * How often an open app reads version.json to see whether a newer build is
 * out, in milliseconds (pwaUpdate).
 * @kept K1
 */
export const VERSION_CHECK_MS = 15 * 60 * 1000;

/**
 * How soon after opening the app first reads version.json, in milliseconds
 * (pwaUpdate), so an app opened from a stale copy hears at once.
 * @kept K1
 */
export const VERSION_FIRST_CHECK_DELAY_MS = 3000;

// ---------------------------------------------------------------------------
// Pages and gestures
// ---------------------------------------------------------------------------

/**
 * How long a page keeps following a #part it was linked to while the page
 * above it fills in, in milliseconds (useArrivalScroll).
 * @kept K5
 */
export const ARRIVAL_SCROLL_FOLLOW_MS = 8000;

/**
 * How long after the last arrow-key press a reordered list is saved, in
 * milliseconds (useDragReorder): several presses in a row are one decision.
 * @kept K5
 */
export const DRAG_REORDER_KEYBOARD_COMMIT_MS = 500;


/**
 * The picture shown where a photo has no address to load from, a path on
 * the app's own host (imageUtils).
 * @kept K1
 */
export const PHOTO_PLACEHOLDER_URL = '/placeholder.jpg';

/**
 * The smallest a crop box may be dragged, on either side, as a fraction of
 * the picture (shared/lib/imageEdit). The server refuses a crop under 32
 * pixels; this keeps the box big enough to grab long before that.
 * @kept K4
 */
export const PHOTO_CROP_MIN_FRACTION = 0.1;

/**
 * How far in from each edge a new Free crop box starts, as a fraction of the
 * picture, so its corners are visibly inside it and easy to find.
 * @kept K4
 */
export const PHOTO_CROP_START_INSET = 0.08;

// ---------------------------------------------------------------------------
// Google: the Photos picker and the geocoder
// ---------------------------------------------------------------------------

/**
 * The one OAuth scope the Photos Picker API uses (GooglePhotosService).
 * @kept K1
 */
export const GOOGLE_PICKER_SCOPE = 'https://www.googleapis.com/auth/photospicker.mediaitems.readonly';

/**
 * The Photos Picker API's base address (GooglePhotosService).
 * @kept K1
 */
export const GOOGLE_PICKER_API_URL = 'https://photospicker.googleapis.com/v1';

/**
 * Google Identity Services' script, loaded on demand by GoogleOneTap. Google
 * Photos no longer uses it: it signs in by sending its own window to Google
 * (GOOGLE_OAUTH_AUTHORIZE_URL), so one tap opens the picker.
 * @kept K1
 */
export const GOOGLE_IDENTITY_SCRIPT_URL = 'https://accounts.google.com/gsi/client';

/**
 * How long to keep asking whether the gardener has chosen a photo in
 * Google's picker, in milliseconds (GooglePhotosService). Long enough to find
 * a photo, short enough that a forgotten tab does not poll all afternoon.
 * @kept K1
 */
export const GOOGLE_PICKER_MAX_WAIT_MS = 5 * 60 * 1000;

/**
 * How often to ask whether a photo has been chosen, in milliseconds, used
 * only when Google's session suggests no interval of its own
 * (GooglePhotosService).
 * @kept K1
 */
export const GOOGLE_PICKER_DEFAULT_POLL_MS = 3000;

/**
 * How many picked photos are listed per page when reading a session back
 * (GooglePhotosService). Google's own maximum is 100.
 * @kept K1
 */
export const GOOGLE_PICKER_PAGE_SIZE = 100;

/**
 * How long a Google access token is taken to last when Google does not say,
 * in seconds (GooglePhotosService).
 * @kept K1
 */
export const GOOGLE_TOKEN_DEFAULT_LIFETIME_S = 3600;

/**
 * How long before Google's stated expiry a held token is dropped, in seconds
 * (GooglePhotosService), so a press never sets off with a token about to die.
 * @kept K1
 */
export const GOOGLE_TOKEN_EXPIRY_MARGIN_S = 60;

/**
 * Google's OAuth sign-in page (googlePhotosFlow). Google Photos' window goes
 * here first when no token is held, and Google sends it back to
 * GOOGLE_PHOTOS_RETURN_PATH with the token.
 * @kept K1
 */
export const GOOGLE_OAUTH_AUTHORIZE_URL = 'https://accounts.google.com/o/oauth2/v2/auth';

/**
 * Where Google sends the Google Photos window back after sign-in, on the
 * app's own address (google-photos.html at the project root). It must be
 * listed, with each origin, under the OAuth client's "Authorized redirect
 * URIs" in the Google Cloud console.
 * @kept K1
 */
export const GOOGLE_PHOTOS_RETURN_PATH = '/google-photos.html';

/**
 * The name of the window Google Photos opens in, so a second tap reuses it
 * rather than opening another (GooglePhotosService).
 * @kept K1
 */
export const GOOGLE_PHOTOS_WINDOW_NAME = 'grow-google-photos';

/**
 * localStorage: the Google Photos sign-in under way in another window — its
 * state check, whether several photos are wanted, and the language
 * (googlePhotosFlow).
 * @kept K1
 */
export const GOOGLE_PHOTOS_REQUEST_KEY = 'grow.googlePhotos.request';

/**
 * localStorage: what the sign-in window hands back to the app's tab — the
 * token and the picking session, or why there are none. Taken and removed at
 * once (googlePhotosFlow).
 * @kept K1
 */
export const GOOGLE_PHOTOS_HANDOFF_KEY = 'grow.googlePhotos.handoff';

/**
 * How long a Google Photos sign-in may take before the app stops waiting for
 * it, in milliseconds (GooglePhotosService). A sign-in or handoff older than
 * this is thrown away.
 * @kept K1
 */
export const GOOGLE_PHOTOS_SIGNIN_MAX_WAIT_MS = 10 * 60 * 1000;

/**
 * localStorage: a Google Photos pick handed over and not yet brought in — the
 * session, its token, whether several photos were wanted and where they were
 * going (googlePhotosFlow's PendingPick). Kept until the photos are in, the
 * gardener cancels, or it runs out, so a phone that reloads or throws away
 * Grow's tab while the gardener is in Google Photos still brings them in.
 * @kept K1
 */
export const GOOGLE_PHOTOS_PENDING_KEY = 'grow.googlePhotos.pending';

/**
 * The longest a handed-over Google Photos pick is kept waiting for the
 * gardener to choose, in milliseconds (googlePhotosFlow). Google's sessions
 * say their own (timeoutIn, about half an hour), and the token's expiry cuts
 * it shorter still; this is the cap when Google says nothing.
 * @kept K1
 */
export const GOOGLE_PHOTOS_PENDING_MAX_MS = 30 * 60 * 1000;

/**
 * How long one tab's hold on a pending Google Photos pick lasts without being
 * renewed, in milliseconds (googlePhotosFlow's claimPending). A tab asking
 * Google renews it at each ask; a tab that was reloaded or thrown away stops,
 * and another may take the pick over once this has passed, so the photos are
 * brought in once and not twice.
 * @kept K1
 */
export const GOOGLE_PHOTOS_PENDING_CLAIM_MS = 20 * 1000;

/**
 * How many times in a row asking Google about the picking session may fail
 * to reach it before the app gives up (googlePhotosFlow). A phone coming back
 * to the app often has no network for its first request.
 * @kept K1
 */
export const GOOGLE_PICKER_NETWORK_RETRIES = 3;

/**
 * The app's own addresses that serve Firebase's sign-in pages themselves
 * (firebase.ts). Each needs https://<host>/__/auth/handler listed on the
 * OAuth client in Google Cloud and on the Facebook app before it is added.
 * @kept K1
 */
export const FIREBASE_OWN_AUTH_HOSTS: readonly string[] = ['grow.gardengameplan.com'];

// ---------------------------------------------------------------------------
// Units
// ---------------------------------------------------------------------------

/** gardengameplan.com's Umami website id, which Grow shares (one site allowed); public, not a secret. */
export const ANALYTICS_UMAMI_WEBSITE_ID = 'e9bb1167-b525-4c5e-b280-6d84d0cb7332';
/** gardengameplan.com's Microsoft Clarity project id, which Grow shares; public, not a secret. */
export const ANALYTICS_CLARITY_PROJECT_ID = 'y1tfhc11t5';
